import { isFirefox } from 'tru-forms-builder';

export interface FileFieldPreparerOptions {
    onBlur?: (element: HTMLElement) => void;
    onChange?: (element: HTMLElement) => void;
}

export class FileFieldPreparer {
    private readonly MAX_FILE_SIZE: number = 28 * 1024 * 1024;

    private selectedFiles: FileList | null = null;
    private hasInteracted = false;

    constructor(
        private readonly fieldElement: HTMLElement,
        private readonly options: FileFieldPreparerOptions = {}
    ) {}

    public get element(): HTMLElement {
        return this.fieldElement;
    }

    public prepare(): void {
        const fieldInputs = this.element.querySelectorAll<HTMLInputElement>('.input, .file-select-label');
        this.prepareListeners(fieldInputs);
    }

    private prepareListeners(fieldInputs: NodeListOf<HTMLInputElement>): void {
        if (!fieldInputs[0] || !fieldInputs[1]) {
            return;
        }

        const fileSelectLabel = fieldInputs[0];
        const fileSelectInput = fieldInputs[1];

        fileSelectLabel?.addEventListener('focus', () => {
            this.hasInteracted = true;
            this.options.onChange?.(this.element);
        });

        const labelElement = this.element.querySelector<HTMLElement>('.label');
        labelElement?.addEventListener('click', (event: Event) => {
            event.preventDefault();
        });

        // NOTE: Not all focus events work the same in all browsers
        if (isFirefox()) {
            document.addEventListener('blur', (event: FocusEvent) => this.handleDocumentBlur(event));
            document.addEventListener('click', (event: Event) => this.handleDocumentClick(event));
        } else {
            fileSelectLabel?.addEventListener('blur', () => {
                if (window.document.hasFocus()) {
                    this.options.onBlur?.(this.element);
                }
            });
        }

        fileSelectInput.onchange = () => {
            if (!fileSelectInput.files) {
                return;
            }

            if (fileSelectInput.files.length === 0) {
                if (this.selectedFiles && this.selectedFiles.length > 0) {
                    fileSelectInput.files = this.selectedFiles;
                }
            } else {
                this.selectedFiles = fileSelectInput.files;
            }

            const filesList = this.getFilesListClean(fileSelectInput);

            for (let i = 0; i < fileSelectInput.files.length; i++) {
                const item = document.createElement('li');
                item.classList.add('item');
                filesList?.append(item);

                this.prepareFilesListItem(fileSelectInput, item, i);

                this.options.onBlur?.(this.element);
            }

            const field = fileSelectInput.closest('.field');
            field?.append(filesList);

            this.options.onChange?.(this.element);
        };

        fileSelectLabel.onkeyup = e => {
            const input = fileSelectLabel.querySelector<HTMLInputElement>('.input');

            if (e.key === 'Enter') {
                input?.click();
                input?.focus();
            }
        };
    }

    private handleDocumentBlur(event: FocusEvent): void {
        if (!this.hasInteracted) {
            return;
        }

        if ((event.relatedTarget instanceof Node) && !this.element.contains(event.relatedTarget)) {
            this.options.onBlur?.(this.element);
            this.hasInteracted = false;
        }
    }

    private handleDocumentClick(event: Event): void {
        if (!this.hasInteracted) {
            return;
        }

        if ((event.target instanceof Node) && this.isClickOutsideFileSelectorArea(event.target as HTMLElement)) {
            this.options.onBlur?.(this.element);
            this.hasInteracted = false;
        }
    }

    private isClickOutsideFileSelectorArea(target: HTMLElement): boolean {
        return !(
            target.nodeType === 1 &&
            this.element.contains(target)
        );
    }

    private getFilesListClean(input: HTMLInputElement): Element {
        let filesList = input.closest('.field')?.querySelector('.files-list');
        if (!filesList) {
            filesList = document.createElement('ul');
            filesList.classList.add('files-list');
        } else {
            filesList.innerHTML = '';
        }

        return filesList;
    }

    private prepareFilesListItem(input: HTMLInputElement, item: HTMLLIElement, fileIndex: number): void {
        const file = input.files?.item(fileIndex);
        if (!file) {
            return;
        }

        const dt = new DataTransfer();
        const content = document.createElement('div');
        content.classList.add('item-content');
        item.append(content);

        const icon = this.createFileIcon(file);
        content.append(icon);

        const name = document.createElement('span');
        name.classList.add('name');
        name.textContent = file.name;
        content.append(name);

        const removeBtn = document.createElement('button');
        removeBtn.setAttribute('type', 'button');
        removeBtn.classList.add('remove-btn');
        removeBtn.onclick = () => {
            item.remove();
            dt.items.remove(fileIndex);
            input.files = dt.files;

            this.options.onBlur?.(this.element);
        };

        const btnIcon = document.createElement('span');
        btnIcon.classList.add('icon', 'icon-close-sm');

        removeBtn.append(btnIcon);

        if (file.size > this.MAX_FILE_SIZE) {
            content.classList.add('error');
            const errorIcon = document.createElement('span');
            errorIcon.classList.add('icon', 'icon-error', 'error-mark');
            content.append(errorIcon);
        }

        content.append(removeBtn);

        dt.items.add(file);
    }

    private createFileIcon(file: File): HTMLSpanElement {
        const icon = document.createElement('span');
        icon.classList.add('icon', 'icon-on-left');
        const fileType = file.name.split('.').at(-1);
        switch (fileType?.toLowerCase()) {
            case 'doc':
            case 'docx': {
                icon.classList.add('icon-doc');
                break;
            }
            case 'jpg':
            case 'png':
            case 'jpeg': {
                icon.classList.add('icon-image');
                break;
            }
            case 'pdf': {
                icon.classList.add('icon-pdf');
                break;
            }
            case 'gif':
            case 'mp4':
            case 'avi':
            case 'mkv':
            case 'wmv':
            case 'flv':
            case 'mpeg': {
                icon.classList.add('icon-mp4');
                break;
            }
            case 'mp3':
            case 'wav':
            case 'midi ':
            case 'aac': {
                icon.classList.add('icon-mp3');
                break;
            }
            case 'accdb': {
                icon.classList.add('icon-accdb');
                break;
            }
            case 'ai': {
                icon.classList.add('icon-ai');
                break;
            }
            case '7z':
            case 'ace':
            case 'arj':
            case 'gz':
            case 'gzip':
            case 'pak':
            case 'rar':
            case 'sit':
            case 'sitx':
            case 'tar':
            case 'tar-gz':
            case 'tgz':
            case 'zip':
            case 'zipx': {
                icon.classList.add('icon-archive');
                break;
            }
            case 'dwg':
            case 'dxf': {
                icon.classList.add('icon-ai');
                break;
            }
            case 'php':
            case 'ts':
            case 'js': {
                icon.classList.add('icon-code');
                break;
            }
            case 'eps': {
                icon.classList.add('icon-eps');
                break;
            }
            case 'key': {
                icon.classList.add('icon-key');
                break;
            }
            case 'numbers': {
                icon.classList.add('icon-numbers');
                break;
            }
            case 'one': {
                icon.classList.add('icon-one');
                break;
            }
            case 'ppt':
            case 'pptx': {
                icon.classList.add('icon-pptx');
                break;
            }
            case 'psd': {
                icon.classList.add('icon-psd');
                break;
            }
            case 'pwl': {
                icon.classList.add('icon-pwl');
                break;
            }
            case 'rtf': {
                icon.classList.add('icon-rtf');
                break;
            }
            case 'svg': {
                icon.classList.add('icon-svg');
                break;
            }
            case 'tiff': {
                icon.classList.add('icon-tiff');
                break;
            }
            case 'ttf': {
                icon.classList.add('icon-ttf');
                break;
            }
            case 'txt': {
                icon.classList.add('icon-txt');
                break;
            }
            case 'xls':
            case 'xlsx': {
                icon.classList.add('icon-xlsx');
                break;
            }
            case 'xps': {
                icon.classList.add('icon-xps');
                break;
            }
            case 'csv': {
                icon.classList.add('icon-csv');
                break;
            }
            default: {
                icon.classList.add('icon-file');
                break;
            }
        }

        return icon;
    }

    public destroy(): void {}
}

export function initializeFileFields(container: HTMLElement, options: FileFieldPreparerOptions = {}): FileFieldPreparer[] {
    const fileFields = container.querySelectorAll<HTMLElement>('[data-element="field"][data-field="file"]');
    const preparers: FileFieldPreparer[] = [];

    fileFields.forEach(field => {
        const preparer = new FileFieldPreparer(field, options);
        preparer.prepare();
        preparers.push(preparer);
    });

    return preparers;
}
